import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// ===== VALIDATION FUNCTIONS (ValidatorFn puras) =====

/**
 * Validator para CPF
 */
export function cpfValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const cpf = control.value.replace(/\D/g, '');

    if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) {
      return { cpfInvalido: true };
    }

    // Algoritmo de validação do CPF
    let soma = 0;
    for (let i = 0; i < 9; i++) {
      soma += parseInt(cpf.charAt(i)) * (10 - i);
    }

    let resto = 11 - (soma % 11);
    let dv1 = resto >= 10 ? 0 : resto;

    if (dv1 !== parseInt(cpf.charAt(9))) {
      return { cpfInvalido: true };
    }

    soma = 0;
    for (let i = 0; i < 10; i++) {
      soma += parseInt(cpf.charAt(i)) * (11 - i);
    }

    resto = 11 - (soma % 11);
    let dv2 = resto >= 10 ? 0 : resto;

    if (dv2 !== parseInt(cpf.charAt(10))) {
      return { cpfInvalido: true };
    }

    return null;
  };
}

/**
 * Validator para CNPJ
 */
export function cnpjValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const cnpj = control.value.replace(/\D/g, '');

    if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) {
      return { cnpjInvalido: true };
    }

    // Algoritmo completo de validação do CNPJ
    const tamanho = cnpj.length - 2;
    const numeros = cnpj.substring(0, tamanho);
    const digitos = cnpj.substring(tamanho);
    let soma = 0;
    let pos = tamanho - 7;

    for (let i = tamanho; i >= 1; i--) {
      soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
      if (pos < 2) pos = 9;
    }

    let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(0))) {
      return { cnpjInvalido: true };
    }

    return null;
  };
}

/**
 * Validator para telefone. Aceita de 8 a 15 dígitos (E.164).
 */
export function telefoneValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const telefone = control.value.replace(/\D/g, '');

    if (telefone.length < 8 || telefone.length > 15) {
      return { telefoneInvalido: true };
    }

    return null;
  };
}

/**
 * Validator para CEP brasileiro
 */
export function cepValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const cep = control.value.replace(/\D/g, '');

    if (cep.length !== 8) {
      return { cepInvalido: true };
    }

    return null;
  };
}

/**
 * Validator para email personalizado
 */
export function emailValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(control.value) ? null : { emailInvalido: true };
  };
}

/**
 * Validator para senhas fortes
 */
export function senhaForteValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const senha = control.value;
    const errors: any = {};

    if (senha.length < 8) {
      errors['minLength'] = { requiredLength: 8, actualLength: senha.length };
    }

    if (!/[A-Z]/.test(senha)) {
      errors['uppercase'] = true;
    }

    if (!/[a-z]/.test(senha)) {
      errors['lowercase'] = true;
    }

    if (!/[0-9]/.test(senha)) {
      errors['number'] = true;
    }

    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(senha)) {
      errors['specialChar'] = true;
    }

    return Object.keys(errors).length > 0 ? { senhaFraca: errors } : null;
  };
}

/**
 * Validator para RG
 */
export function rgValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const rg = control.value.replace(/[^0-9X]/g, '');

    if (rg.length !== 9) {
      return { rgInvalido: true };
    }

    return null;
  };
}

/**
 * Validator para PIS/PASEP
 */
export function pisValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const pis = control.value.replace(/\D/g, '');

    if (pis.length !== 11) {
      return { pisInvalido: true };
    }

    // Algoritmo de validação do PIS
    const pesos = [3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let soma = 0;

    for (let i = 0; i < 10; i++) {
      soma += parseInt(pis[i]) * pesos[i];
    }

    const resto = soma % 11;
    const digito = resto < 2 ? 0 : 11 - resto;

    if (digito !== parseInt(pis[10])) {
      return { pisInvalido: true };
    }

    return null;
  };
}

/**
 * Validator para placa de veículo (antiga e Mercosul)
 */
export function placaValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const placa = control.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

    // Placa antiga: AAA0000
    const placaAntiga = /^[A-Z]{3}\d{4}$/;

    // Placa Mercosul: AAA0A00
    const placaMercosul = /^[A-Z]{3}\d{1}[A-Z]{1}\d{2}$/;

    if (!placaAntiga.test(placa) && !placaMercosul.test(placa)) {
      return { placaInvalida: true };
    }

    return null;
  };
}

/**
 * Validator para confirmar senha (deve ser usado em FormGroup)
 */
export function confirmarSenhaValidator(
  senhaField: string,
  confirmarField: string
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.parent) return null;

    const senha = control.parent.get(senhaField);
    const confirmar = control.parent.get(confirmarField);

    if (!senha || !confirmar) return null;

    if (senha.value !== confirmar.value) {
      return { senhasNaoConferem: true };
    }

    return null;
  };
}

// ===== HELPER VALIDATION FUNCTIONS =====

/**
 * Valida CPF (retorna boolean)
 */
export function validarCPF(cpf: string): boolean {
  const validator = cpfValidator();
  const control = { value: cpf } as AbstractControl;
  return validator(control) === null;
}

/**
 * Valida CNPJ (retorna boolean)
 */
export function validarCNPJ(cnpj: string): boolean {
  const validator = cnpjValidator();
  const control = { value: cnpj } as AbstractControl;
  return validator(control) === null;
}

/**
 * Valida telefone (retorna boolean)
 */
export function validarTelefone(telefone: string): boolean {
  const validator = telefoneValidator();
  const control = { value: telefone } as AbstractControl;
  return validator(control) === null;
}

/**
 * Valida CEP (retorna boolean)
 */
export function validarCEP(cep: string): boolean {
  const validator = cepValidator();
  const control = { value: cep } as AbstractControl;
  return validator(control) === null;
}

/**
 * Valida email (retorna boolean)
 */
export function validarEmail(email: string): boolean {
  const validator = emailValidator();
  const control = { value: email } as AbstractControl;
  return validator(control) === null;
}

/**
 * Valida RG (retorna boolean)
 */
export function validarRG(rg: string): boolean {
  const validator = rgValidator();
  const control = { value: rg } as AbstractControl;
  return validator(control) === null;
}

/**
 * Valida PIS (retorna boolean)
 */
export function validarPIS(pis: string): boolean {
  const validator = pisValidator();
  const control = { value: pis } as AbstractControl;
  return validator(control) === null;
}

/**
 * Valida placa (retorna boolean)
 */
export function validarPlaca(placa: string): boolean {
  const validator = placaValidator();
  const control = { value: placa } as AbstractControl;
  return validator(control) === null;
}

// ===== CLASSE ESTÁTICA (para organização e compatibilidade) =====

/**
 * Classe estática com todos os validators e funções de validação
 * Use quando preferir organização por namespace: ValidationUtils.cpfValidator()
 */
export class ValidationUtils {
  // Validators
  static cpfValidator = cpfValidator;
  static cnpjValidator = cnpjValidator;
  static telefoneValidator = telefoneValidator;
  static cepValidator = cepValidator;
  static emailValidator = emailValidator;
  static senhaForteValidator = senhaForteValidator;
  static rgValidator = rgValidator;
  static pisValidator = pisValidator;
  static placaValidator = placaValidator;
  static confirmarSenhaValidator = confirmarSenhaValidator;

  // Helper functions
  static validarCPF = validarCPF;
  static validarCNPJ = validarCNPJ;
  static validarTelefone = validarTelefone;
  static validarCEP = validarCEP;
  static validarEmail = validarEmail;
  static validarRG = validarRG;
  static validarPIS = validarPIS;
  static validarPlaca = validarPlaca;
}
